// Pure (no React) description of what an Edit save, or a Create submit, will do. Computed
// client-side on purpose: the API's own `changes` flag can report a false pricingPlanChanged
// because its merge includes server defaults.

import { BILLING_MODE } from '../../../constants';

export const GROUP = {
  PERSONAL: 'Personal details',
  BILLING: 'Billing settings',
  SEAT: 'Seat pricing',
  PAYG: 'Pay-As-You-Go pricing',
};

// Mirrors the formatCurrency in AddCompany / UpdateCompany.
export function formatCurrency(amount) {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
}

const fmt = {
  text: (v) => {
    const s = String(v ?? '').trim();
    return s === '' ? '—' : s;
  },
  money: (v) => formatCurrency(Number(v) || 0),
  bool: (v) => (v ? 'On' : 'Off'),
  day: (v) => `day ${v} of the month`,
  upper: (v) => String(v || '').toUpperCase() || '—',
};

const sameText = (a, b) => String(a ?? '').trim() === String(b ?? '').trim();
// Form inputs hold strings while the loaded record holds numbers; '75' and 75 are the same price.
// A missing value is NaN on both sides and counts as unchanged.
const sameNumber = (a, b) => {
  const x = Number(a);
  const y = Number(b);
  return x === y || (Number.isNaN(x) && Number.isNaN(y));
};

const row = (key, label, from, to, changed, format) => ({ key, label, from, to, changed, format });

const textRow = (key, label, o, c) => row(key, label, o, c, !sameText(o, c), fmt.text);
const moneyRow = (key, label, o, c) => row(key, label, o, c, !sameNumber(o, c), fmt.money);
const boolRow = (key, label, o, c) => row(key, label, !!o, !!c, !!o !== !!c, fmt.bool);

function phoneOf(v) {
  return `${v.countryCode || ''}${v.phoneNo || ''}`;
}

function personalRows(o, c) {
  return [
    textRow('username', 'Username', o.username, c.username),
    textRow('email', 'Email', o.email, c.email),
    // countryCode + phoneNo travel together: one shown value, changed if either half moved.
    row(
      'phone',
      'Phone',
      phoneOf(o),
      phoneOf(c),
      !sameText(o.phoneNo, c.phoneNo) || !sameText(o.countryCode, c.countryCode),
      fmt.text
    ),
  ];
}

function flagRows(o, c) {
  return [
    boolRow('validPaymentMethodRequired', 'Auto Pay (card on file required)', o.validPaymentMethodRequired, c.validPaymentMethodRequired),
    boolRow('trialModeEnabled', 'Free trial', o.trialModeEnabled, c.trialModeEnabled),
    boolRow('legacyCompany', 'Legacy company', o.legacyCompany, c.legacyCompany),
  ];
}

const SEAT_FIELDS = [
  ['baseCharge', 'Base charge / month'],
  ['companyUser', 'Company user'],
  ['readWrite', 'Read/Write user'],
  ['topo', 'Topo user (seat)'],
  ['readOnly', 'Read-only user'],
  ['foundationAnalysisAddOn', 'Foundation Analysis add-on'],
  ['floorLevelAddOn', 'Floor Level add-on'],
];

function seatRows(o, c) {
  const op = o.pricingPlan || {};
  const cp = c.pricingPlan || {};
  return [
    ...SEAT_FIELDS.map(([key, label]) => moneyRow(`pricingPlan.${key}`, label, op[key], cp[key])),
    row('pricingPlan.currency', 'Currency', op.currency, cp.currency, !sameText(op.currency, cp.currency), fmt.upper),
  ];
}

function paygRows(o, c) {
  const op = o.paygPlan || {};
  const cp = c.paygPlan || {};
  return [
    moneyRow('paygPlan.baseCharge', 'Base charge / month', op.baseCharge, cp.baseCharge),
    moneyRow('paygPlan.pricePerTopo', 'Price per topo', op.pricePerTopo, cp.pricePerTopo),
    row('paygPlan.invoiceDayOfMonth', 'Invoice day', op.invoiceDayOfMonth, cp.invoiceDayOfMonth, !sameNumber(op.invoiceDayOfMonth, cp.invoiceDayOfMonth), fmt.day),
    row('paygPlan.currency', 'Currency', op.currency, cp.currency, !sameText(op.currency, cp.currency), fmt.upper),
  ];
}

const anyChanged = (rows) => rows.some((r) => r.changed);
const changedKeys = (rows) => rows.filter((r) => r.changed).map((r) => r.key);

const PARTIAL_SAVE_TEXT =
  'Changes are saved in sequence (profile, trial, pricing, auto-pay). If a later step fails, earlier steps may already be saved.';

export function buildChangeSet(original, current, ctx = {}) {
  const o = original || {};
  const c = current || {};
  const { billingMode = BILLING_MODE.SEAT, hasStripeSubscription = false, hasStripeCustomer = false } = ctx;
  const isPayg = billingMode === BILLING_MODE.PAYG;

  const personal = personalRows(o, c);
  const flags = flagRows(o, c);
  const pricing = isPayg ? paygRows(o, c) : seatRows(o, c);

  const groups = [
    { group: GROUP.PERSONAL, rows: personal },
    { group: GROUP.BILLING, rows: flags },
    isPayg ? { group: GROUP.PAYG, rows: pricing } : { group: GROUP.SEAT, rows: pricing },
  ];

  const emailChanged = !sameText(o.email, c.email);
  const autoPayWas = !!o.validPaymentMethodRequired;
  const autoPayNow = !!c.validPaymentMethodRequired;
  const trialWas = !!o.trialModeEnabled;
  const trialNow = !!c.trialModeEnabled;
  const legacyWas = !!o.legacyCompany;
  const legacyNow = !!c.legacyCompany;
  const pricingChanged = anyChanged(pricing);

  const effects = [];
  const info = (text) => effects.push({ level: 'info', text });
  const warn = (text) => effects.push({ level: 'warning', text });

  if (emailChanged && hasStripeCustomer) {
    info('The email on the Stripe customer record is also updated.');
  }

  if (!isPayg && pricingChanged) {
    if (autoPayWas && autoPayNow && hasStripeSubscription) {
      info('Live Stripe subscription items are rewritten to the new prices. No proration is created; the new prices apply from the next billing cycle.');
    } else if (autoPayWas || !autoPayNow) {
      info('Seat prices are saved to the database only; there is no live Stripe subscription to update.');
    }
    // Auto Pay OFF -> ON with a price change is the one case left out: the subscription is
    // created from the saved prices, which the Auto Pay effect below already describes.
  }

  if (isPayg) {
    const rateChanged = changedKeys(pricing).some((k) => k === 'paygPlan.baseCharge' || k === 'paygPlan.pricePerTopo');
    if (rateChanged) {
      info('Pay-As-You-Go prices have no effect in Stripe. A new per-topo price applies to topos created from now on; existing topos keep the price they were created at.');
    }
    if (changedKeys(pricing).includes('paygPlan.invoiceDayOfMonth')) {
      info('The next invoice date moves to match the new invoice day.');
    }
  }

  if (trialWas && !trialNow) {
    if (isPayg) {
      warn('Ending the trial: the subscription becomes active if a card is on file, otherwise pending. New topos are billed at the per-topo price immediately; topos already created during the trial stay free.');
    } else if (hasStripeSubscription) {
      warn('Ends the Stripe trial now and may bill the customer immediately.');
    }
  } else if (!trialWas && trialNow) {
    info('A new trial period starts.');
  }

  if (!autoPayWas && autoPayNow) {
    if (isPayg) {
      info('Auto Pay is turned on. Pay-As-You-Go companies have no Stripe subscription; the card is managed in the company\'s Admin app.');
    } else {
      info('A Stripe subscription is created and a payment link is emailed to the company.');
    }
  } else if (autoPayWas && !autoPayNow) {
    if (isPayg) {
      warn('Auto Pay is turned off.');
    } else {
      warn('The Stripe subscription is cancelled.');
    }
  }

  if (!legacyWas && legacyNow) {
    info('Foundation Analysis becomes $0 for this company.');
  } else if (legacyWas && !legacyNow) {
    info('Foundation Analysis is restored to the server default, which may differ from the price shown in the form.');
  }

  const personalOrFlagsChanged = anyChanged(personal) || anyChanged(flags);
  if (personalOrFlagsChanged && pricingChanged) {
    warn(PARTIAL_SAVE_TEXT);
  }

  const hasChanges = personalOrFlagsChanged || pricingChanged;
  return { groups, effects, hasChanges };
}

// Plain-language lines for the Create step-2 "What will happen" box.
export function buildCreateSummary(current, opts = {}) {
  const c = current || {};
  const billingMode = opts.billingMode || c.billingMode || BILLING_MODE.SEAT;
  const email = String(c.email || '').trim() || 'the company email';
  const lines = [`A welcome email with a temporary password is sent to ${email}.`];

  if (billingMode === BILLING_MODE.PAYG) {
    lines.push('No subscription is created. The company adds a card in its Admin app.');
    lines.push('Per-topo billing starts now.');
    if (c.trialModeEnabled) {
      lines.push('The trial is on: topos created during the trial are free.');
    }
  } else if (c.validPaymentMethodRequired) {
    lines.push(
      `A Stripe Checkout link is emailed to the company${c.trialModeEnabled ? ' (a trial applies)' : ''}. The subscription stays pending until it is paid.`
    );
  } else {
    lines.push('A Stripe customer is created, but no subscription (manual pay).');
  }

  if (c.legacyCompany) {
    lines.push('Legacy company: Foundation Analysis is free.');
  }
  return lines;
}

// Drives the "modified" dot on each tab.
export function isDirtyByTab(original, current) {
  const o = original || {};
  const c = current || {};
  return {
    personal: anyChanged(personalRows(o, c)),
    billing: anyChanged(flagRows(o, c)) || anyChanged(seatRows(o, c)) || anyChanged(paygRows(o, c)),
  };
}
